import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { neon } from "@/integrations/neon";
import { getErrorMessage } from "@/utils/errors";

interface AuthPageProps {
  onAuthSuccess: () => void;
}

type AuthMode = "signIn" | "signUp" | "reset";

const AUTH_COPY: Record<AuthMode, {
  title: string;
  description: string;
  submitLabel: string;
  togglePrompt: string;
  toggleAction: string;
  toggleTarget: AuthMode;
}> = {
  signIn: {
    title: "Welcome back",
    description: "Enter your credentials to access your dashboard",
    submitLabel: "Sign In",
    togglePrompt: "Don't have an account?",
    toggleAction: "Sign up",
    toggleTarget: "signUp",
  },
  signUp: {
    title: "Create your account",
    description: "Start collecting professional script feedback",
    submitLabel: "Create Account",
    togglePrompt: "Already have an account?",
    toggleAction: "Sign in",
    toggleTarget: "signIn",
  },
  reset: {
    title: "Reset your password",
    description: "Enter your email to receive password reset instructions",
    submitLabel: "Send Reset Email",
    togglePrompt: "Remember your password?",
    toggleAction: "Sign in",
    toggleTarget: "signIn",
  },
};

const sendPasswordReset = async (email: string) => {
  const { error } = await neon.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/`,
  });

  if (error) throw error;
};

const registerUser = async (email: string, password: string, name: string) => {
  const { error } = await neon.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: `${window.location.origin}/`,
      data: { name },
    },
  });

  if (error) throw error;
};

const signInUser = async (email: string, password: string) => {
  const { error } = await neon.auth.signInWithPassword({
    email,
    password,
  });

  if (error) throw error;
};

export const AuthPage = ({ onAuthSuccess }: AuthPageProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<AuthMode>("signIn");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const { toast } = useToast();

  const clearForm = () => {
    setEmail("");
    setPassword("");
    setName("");
  };

  const switchMode = (nextMode: AuthMode) => {
    setMode(nextMode);
    setResetSent(false);
    clearForm();
  };

  const copy = AUTH_COPY[mode];
  const isReset = mode === "reset";
  const isSignUp = mode === "signUp";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isReset) {
        await sendPasswordReset(email);
        setResetSent(true);
        toast({
          title: "Password reset email sent!",
          description: "Please check your email for reset instructions.",
        });
        return;
      }

      if (isSignUp) {
        await registerUser(email, password, name);
        toast({
          title: "Account created successfully!",
          description: "Welcome to Story Gauge.",
        });
        onAuthSuccess();
        return;
      }

      await signInUser(email, password);
      toast({
        title: "Welcome back!",
        description: "You've been successfully logged in.",
      });
      onAuthSuccess();
    } catch (error: unknown) {
      toast({
        title: "Authentication Error",
        description: getErrorMessage(error, "Unable to complete authentication"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white/5 p-3 rounded-lg shadow-medium">
              <img
                src="/lovable-uploads/30cb2c33-4a20-4489-831b-007a49a4be5f.png"
                alt="Benetone Films Logo"
                className="h-16 w-16 object-contain"
              />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Benetone Films Script Assessment</h1>
          <p className="text-muted-foreground">Professional script feedback platform</p>
        </div>

        <Card className="shadow-strong border-0">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">{copy.title}</CardTitle>
            <CardDescription className="text-center">{copy.description}</CardDescription>
          </CardHeader>
          <CardContent>
            {resetSent ? (
              <div className="text-center space-y-4">
                <p className="text-sm text-muted-foreground">
                  We've sent you a password reset link. Please check your email and follow the instructions to reset your password.
                </p>
                <Button
                  variant="outline"
                  onClick={() => switchMode("signIn")}
                  className="w-full"
                >
                  Back to Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {isSignUp && !isReset && (
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Enter your full name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="transition-all duration-200 focus:shadow-soft"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="transition-all duration-200 focus:shadow-soft"
                  />
                </div>

                {!isReset && (
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="transition-all duration-200 focus:shadow-soft"
                    />
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-gradient-primary hover:opacity-90 transition-all duration-200 shadow-medium hover:shadow-strong"
                  disabled={isLoading}
                >
                  {isLoading ? "Processing..." : copy.submitLabel}
                </Button>
              </form>
            )}

            {!resetSent && (
              <div className="mt-6 text-center space-y-3">
                {!isReset && (
                  <div>
                    <Button
                      variant="ghost"
                      className="text-sm text-primary hover:text-primary-hover"
                      onClick={() => switchMode("reset")}
                    >
                      Forgot your password?
                    </Button>
                  </div>
                )}

                <p className="text-sm text-muted-foreground">
                  {copy.togglePrompt}
                  <Button
                    variant="ghost"
                    className="ml-1 p-0 h-auto font-semibold text-primary hover:text-primary-hover"
                    onClick={() => switchMode(copy.toggleTarget)}
                  >
                    {copy.toggleAction}
                  </Button>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
